import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Paper,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Typography,
  InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import {
  getAvailableClients,
  addClientToAccessGroup,
  removeClientFromAccessGroup,
  updateClientNickname,
  Client
} from 'services/user';
import api from 'services/api';
import DataTable from 'components/Shared/Table';
import Loader from 'components/Shared/Loader';
import type { MUIDataTableState } from 'mui-datatables';
import { primary } from 'helpers/colors';

type Props = {
  accessGroupId: number;
  onRefresh: () => void;
};

function ClientsTab({ accessGroupId, onRefresh }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [checkedClientIds, setCheckedClientIds] = useState<number[]>([]);
  const [modalSearch, setModalSearch] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [clientToRemove, setClientToRemove] = useState<Client | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [clientToEdit, setClientToEdit] = useState<Client | null>(null);
  const [editNickname, setEditNickname] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sortField, setSortField] = useState<string>('cnpj');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const loadClients = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get<{ data: { clients: Client[]; total: number } }>(
        `/access_groups/${accessGroupId}/clients`,
        {
          perPage: rowsPerPage,
          page: page,
          search: searchText,
          order: sortField,
          direction: sortDirection
        }
      );

      if (response.data?.clients) {
        setClients(response.data.clients);
        setTotalCount(response.data?.total || 0);
      }
      setIsLoading(false);
    } catch (error) {
      enqueueSnackbar('Erro ao carregar clientes', { variant: 'error' });
      setIsLoading(false);
    }
  }, [accessGroupId, enqueueSnackbar, page, rowsPerPage, searchText, sortField, sortDirection]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const loadAvailableClients = useCallback(() => {
    setIsFetching(true);
    getAvailableClients(
      accessGroupId,
      data => {
        setAvailableClients(data);
        setIsFetching(false);
      },
      error => {
        enqueueSnackbar(error, { variant: 'error' });
        setIsFetching(false);
      }
    );
  }, [accessGroupId, enqueueSnackbar]);

  const handleAddClick = () => {
    setModalSearch('');
    setCheckedClientIds([]);
    loadAvailableClients();
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setModalSearch('');
    setCheckedClientIds([]);
  };

  const handleToggleClient = (clientId: number) => {
    setCheckedClientIds(prev => (prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]));
  };

  const handleAddClients = () => {
    if (checkedClientIds.length === 0) return;

    setIsAdding(true);
    const clientsToAdd = availableClients.filter(c => checkedClientIds.includes(c.id));
    const addSequentially = clientsToAdd.map(
      client => () =>
        new Promise<void>((resolve, reject) => {
          addClientToAccessGroup(
            accessGroupId,
            client.id,
            () => resolve(),
            error => reject(new Error(error))
          );
        })
    );

    addSequentially
      .reduce((promise: Promise<void>, fn: () => Promise<void>) => promise.then(() => fn()), Promise.resolve())
      .then(() => {
        setIsAdding(false);
        enqueueSnackbar('Clientes adicionados com sucesso', { variant: 'success' });
        handleCloseAddModal();
        loadClients();
        onRefresh();
      })
      .catch(() => {
        setIsAdding(false);
        enqueueSnackbar('Erro ao adicionar clientes', { variant: 'error' });
      });
  };

  const handleRemoveClick = useCallback((client: Client) => {
    setClientToRemove(client);
  }, []);

  const confirmRemoveClient = useCallback(async () => {
    if (!clientToRemove) return;
    setIsRemoving(true);
    removeClientFromAccessGroup(
      accessGroupId,
      clientToRemove.id,
      () => {
        setIsRemoving(false);
        enqueueSnackbar('Cliente removido com sucesso', { variant: 'success' });
        setClientToRemove(null);
        loadClients();
        onRefresh();
      },
      error => {
        setIsRemoving(false);
        enqueueSnackbar(error, { variant: 'error' });
      }
    );
  }, [clientToRemove, accessGroupId, enqueueSnackbar, loadClients, onRefresh]);

  const handleEditClick = useCallback((client: Client) => {
    setClientToEdit(client);
    setEditNickname(client.nickname || '');
  }, []);

  const handleSaveNickname = useCallback(() => {
    if (!clientToEdit) return;
    setIsSaving(true);
    updateClientNickname(
      clientToEdit.id,
      editNickname,
      clientToEdit,
      () => {
        setIsSaving(false);
        enqueueSnackbar('Nome atualizado com sucesso', { variant: 'success' });
        setClientToEdit(null);
        loadClients();
        onRefresh();
      },
      error => {
        setIsSaving(false);
        enqueueSnackbar(error, { variant: 'error' });
      }
    );
  }, [clientToEdit, editNickname, enqueueSnackbar, loadClients, onRefresh]);

  const formatCNPJ = (cnpj: string) => {
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const formattedData = useMemo(() => [...clients], [clients]);

  const renderActions = useCallback(
    (_value: any, tableMeta: any) => {
      const client = clients[tableMeta.rowIndex];
      if (!client) return null;

      return (
        <>
          <Tooltip title='Editar nome'>
            <IconButton size='small' color='primary' onClick={() => handleEditClick(client)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title='Remover'>
            <IconButton size='small' color='error' onClick={() => handleRemoveClick(client)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      );
    },
    [clients, handleEditClick, handleRemoveClick]
  );

  const columns = useMemo(
    () => [
      {
        name: 'cnpj',
        label: 'CNPJ',
        options: {
          sort: true,
          customBodyRender: (value: string) => formatCNPJ(value),
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      },
      {
        name: 'razao_social',
        label: 'Razão Social',
        options: {
          sort: true,
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      },
      {
        name: 'nickname',
        label: 'Nome',
        options: {
          sort: false,
          customBodyRender: (v: string) => v || '-',
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      },
      {
        name: 'id',
        label: 'Ações',
        options: {
          sort: false,
          customBodyRender: renderActions,
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      }
    ],
    [renderActions]
  );

  const handleTableChange = useCallback((action: string, tableState: MUIDataTableState) => {
    switch (action) {
      case 'changePage':
        setPage(tableState.page + 1);
        break;
      case 'changeRowsPerPage':
        setRowsPerPage(tableState.rowsPerPage);
        setPage(1);
        break;
      case 'search':
        setSearchText(tableState.searchText || '');
        setPage(1);
        break;
      case 'sort':
        if (tableState.sortOrder?.name && tableState.sortOrder?.direction) {
          setSortField(tableState.sortOrder.name);
          setSortDirection(tableState.sortOrder.direction);
          setPage(1);
        }
        break;
      default:
        console.info(`action (${action}) not handled.`);
    }
  }, []);

  const optionsObj = useMemo(
    () => ({
      page: page - 1,
      count: totalCount,
      rowsPerPage,
      jumpToPage: true,
      onTableChange: handleTableChange,
      serverSide: true,
      search: false,
      searchText,
      selectableRows: 'none',
      sortOrder: { name: sortField, direction: sortDirection }
    }),
    [page, totalCount, rowsPerPage, handleTableChange, searchText, sortField, sortDirection]
  );

  return (
    <Box>
      <Box mb={0} display='flex' gap={2} mx={2} alignItems='center'>
        <TextField
          value={searchText}
          onChange={e => {
            setSearchText(e.target.value);
            setPage(1);
          }}
          placeholder='Pesquisar por CNPJ, Razão Social ou Nome'
          variant='outlined'
          size='small'
          sx={{
            input: { fontSize: '0.9rem' },
            flex: 1,
            '& .MuiOutlinedInput-root': { borderRadius: 10 }
          }}
        />
        <Button
          variant='contained'
          color='primary'
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disableElevation
          sx={{
            borderRadius: 10,
            whiteSpace: 'nowrap',
            py: 0.7,
            '&:hover': { backgroundColor: primary.hover }
          }}
        >
          Adicionar Cliente
        </Button>
      </Box>

      <Card elevation={0}>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            {isLoading ? <Loader /> : <DataTable columns={columns} data={formattedData} optionsObj={optionsObj} />}
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={addModalOpen} onClose={!isAdding ? handleCloseAddModal : undefined} maxWidth='sm' fullWidth>
        <DialogTitle>Adicionar Clientes</DialogTitle>
        <DialogContent dividers>
          <Box>
            <TextField
              fullWidth
              size='small'
              placeholder='Buscar por CNPJ, Razão Social ou Nome'
              value={modalSearch}
              onChange={e => setModalSearch(e.target.value)}
              disabled={isAdding}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' color='action' />
                  </InputAdornment>
                )
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 10 } }}
            />
            <Box mt={1.5} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              {isFetching && (
                <Box display='flex' justifyContent='center' py={4}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {!isFetching && availableClients.length === 0 && (
                <Box py={4} textAlign='center'>
                  <Typography variant='body2' color='text.secondary'>
                    Nenhum cliente disponível
                  </Typography>
                </Box>
              )}
              {!isFetching &&
                availableClients.length > 0 &&
                (() => {
                  const q = modalSearch.toLowerCase();
                  const filtered = availableClients.filter(
                    c =>
                      c.cnpj.includes(q) ||
                      c.razao_social.toLowerCase().includes(q) ||
                      (c.nickname || '').toLowerCase().includes(q)
                  );
                  return filtered.length === 0 ? (
                    <Box py={4} textAlign='center'>
                      <Typography variant='body2' color='text.secondary'>
                        Nenhum cliente encontrado
                      </Typography>
                    </Box>
                  ) : (
                    <List dense disablePadding sx={{ maxHeight: 320, overflow: 'auto' }}>
                      {filtered.map(client => (
                        <ListItemButton
                          key={client.id}
                          onClick={() => handleToggleClient(client.id)}
                          disabled={isAdding}
                          sx={{ px: 1.5 }}
                        >
                          <Checkbox
                            edge='start'
                            checked={checkedClientIds.includes(client.id)}
                            tabIndex={-1}
                            disableRipple
                            size='small'
                            sx={{ mr: 1 }}
                          />
                          <ListItemText
                            primary={client.nickname || client.razao_social}
                            secondary={`${formatCNPJ(client.cnpj)}${
                              client.nickname ? ` — ${client.razao_social}` : ''
                            }`}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  );
                })()}
            </Box>
            {checkedClientIds.length > 0 && (
              <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
                {checkedClientIds.length} cliente{checkedClientIds.length > 1 ? 's' : ''} selecionado
                {checkedClientIds.length > 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '14px 20px' }}>
          <Button onClick={handleCloseAddModal} disabled={isAdding}>
            Cancelar
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleAddClients}
            disabled={isAdding || checkedClientIds.length === 0}
            disableElevation
            startIcon={isAdding ? <CircularProgress size={16} color='inherit' /> : null}
            sx={{ borderRadius: 10, whiteSpace: 'nowrap', py: 0.7, '&:hover': { backgroundColor: primary.hover } }}
          >
            {isAdding ? 'Salvando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={clientToEdit !== null} onClose={() => !isSaving && setClientToEdit(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Editar Nome</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <TextField
              label='Nome'
              value={editNickname}
              onChange={e => setEditNickname(e.target.value)}
              fullWidth
              size='small'
              autoFocus
              onKeyDown={e => e.key === 'Enter' && !isSaving && handleSaveNickname()}
            />
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '14px 20px' }}>
          <Button onClick={() => setClientToEdit(null)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleSaveNickname}
            disabled={isSaving}
            disableElevation
            startIcon={isSaving ? <CircularProgress size={16} color='inherit' /> : null}
            sx={{ borderRadius: 10, whiteSpace: 'nowrap', py: 0.7, '&:hover': { backgroundColor: primary.hover } }}
          >
            {isSaving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={clientToRemove !== null} onClose={() => !isRemoving && setClientToRemove(null)}>
        <DialogTitle>Remover cliente</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja remover o cliente{' '}
            <strong>{clientToRemove?.nickname || clientToRemove?.razao_social}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ paddingTop: 5, marginBottom: 10, marginRight: 12 }}>
          <Button onClick={() => setClientToRemove(null)} disabled={isRemoving}>
            Cancelar
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={confirmRemoveClient}
            disabled={isRemoving}
            disableElevation
            startIcon={isRemoving ? <CircularProgress size={16} color='inherit' /> : null}
            sx={{ borderRadius: 10, whiteSpace: 'nowrap' }}
          >
            {isRemoving ? 'Removendo...' : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ClientsTab;
